'use client'

/**
 * Phosphor-compatible icon shim over the HugeIcons free stroke set.
 *
 * This is the ONLY file in trellis-app that imports `@hugeicons/*`. Every
 * export is a React component named after the `@phosphor-icons/react` icon it
 * replaces and accepts the Phosphor prop surface (`size`, `color`, `weight`,
 * `mirrored`, `alt`, plain SVG props, `ref`), so consumers migrate with an
 * import-path change only. Upgrading to HugeIcons Pro (or a solid style) is a
 * one-file swap: change the package import and the per-name mappings below.
 *
 * Weight semantics (DESIGN.md icon rule - HugeIcons stroke style, 1.2 stroke):
 * the free set is stroke-only, so Phosphor's fill/duotone/thin/light weights
 * cannot change the glyph style. 'bold' renders strokeWidth 1.8 (emphasis);
 * every other weight ('thin' | 'light' | 'regular' | 'fill' | 'duotone')
 * renders the standard 1.2.
 *
 * Stroke width is a VISUAL spec (rendered px). The glyphs live in a 24-unit
 * viewBox, so a raw stroke of 1.2 at a 16px icon would paint a washed-out
 * 0.8px hairline. Every path is pinned with `vector-effect:
 * non-scaling-stroke`, so the apparent stroke stays at the requested weight
 * at every rendered size - whether sized via the `size` prop or width/height
 * utility classes. (Same approach as trellis-gtm's shim, self-contained here
 * instead of a globals.css rule.)
 *
 * `// icon-review: resolved` marks mappings that were audited against the
 * full free set: either the free set has no faithful glyph and the closest
 * semantic neighbor was deliberately kept, or a better glyph was chosen.
 */

import {
  Activity01Icon,
  Add01Icon,
  AddressBookIcon,
  Agreement01Icon,
  Airplane01Icon,
  Album01Icon,
  Alert01Icon,
  AlertCircleIcon,
  AlertDiamondIcon,
  AngryIcon,
  AppWindowIcon,
  ArchiveIcon,
  ArmchairIcon,
  ArrowDown01Icon,
  ArrowDown02Icon,
  ArrowDownDoubleIcon,
  ArrowDownToLineIcon,
  ArrowExpand01Icon,
  ArrowExpandIcon,
  ArrowHorizontalIcon,
  ArrowLeft01Icon,
  ArrowLeft02Icon,
  ArrowLeftDoubleIcon,
  ArrowLeftRightIcon,
  ArrowRight01Icon,
  ArrowRight02Icon,
  ArrowRightDoubleIcon,
  ArrowRightToLineIcon,
  ArrowShrink01Icon,
  ArrowShrinkIcon,
  ArrowTurnBackwardIcon,
  ArrowTurnForwardIcon,
  ArrowUp01Icon,
  ArrowUp02Icon,
  ArrowUpDownIcon,
  ArrowUpRight01Icon,
  ArrowUpToLineIcon,
  AtIcon,
  AttachmentIcon,
  AudioWave01Icon,
  Baby01Icon,
  BankIcon,
  Bathtub01Icon,
  BatteryFullIcon,
  BatteryLowIcon,
  BedIcon,
  BellRingIcon,
  Bookmark01Icon,
  BookOpen01Icon,
  BookOpenTextIcon,
  BotIcon,
  BrainIcon,
  BrickWallIcon,
  Briefcase01Icon,
  BrowserIcon,
  BubbleChatIcon,
  Bug01Icon,
  Building01Icon,
  Building06Icon,
  BulbIcon,
  Calendar01Icon,
  Calendar02Icon,
  Calendar03Icon,
  CalendarAdd01Icon,
  CalendarCheckIcon,
  CalendarRemove01Icon,
  CallBlockedIcon,
  CallDisabledIcon,
  CallIcon,
  CallIncoming01Icon,
  CallInternalIcon,
  CallOutgoing01Icon,
  CallRinging01Icon,
  Camera01Icon,
  Cancel01Icon,
  CancelCircleIcon,
  Car01Icon,
  Cardiogram01Icon,
  ChampionIcon,
  ChartColumnIcon,
  ChartLineIcon,
  ChartUpIcon,
  Chatting01Icon,
  CheckListIcon,
  CheckmarkBadge01Icon,
  CheckmarkCircle01Icon,
  CheckmarkSquare01Icon,
  CircleArrowUp01Icon,
  CircleDashedIcon,
  CircleIcon,
  CleanIcon,
  Clock01Icon,
  CloudUploadIcon,
  CodeIcon,
  Coffee01Icon,
  Coins01Icon,
  Comment01Icon,
  Comment02Icon,
  CompassIcon,
  ComputerIcon,
  ConversationIcon,
  CookingPotIcon,
  Copy01Icon,
  Copy02Icon,
  CornerDownRightIcon,
  CpuIcon,
  CreditCardIcon,
  CrownIcon,
  Csv01Icon,
  CubeIcon,
  CursorRectangleSelection01Icon,
  DashboardSpeed01Icon,
  DatabaseIcon,
  Delete02Icon,
  DocumentCodeIcon,
  Dollar01Icon,
  DollarCircleIcon,
  Door01Icon,
  Download01Icon,
  DraftingCompassIcon,
  DragDropHorizontalIcon,
  DragDropVerticalIcon,
  DropletIcon,
  Dumbbell01Icon,
  ElectricPlugsIcon,
  EuroIcon,
  Fan01Icon,
  FastWindIcon,
  FavouriteIcon,
  File01Icon,
  File02Icon,
  FileAddIcon,
  FileCodeIcon,
  FileExportIcon,
  FileUploadIcon,
  FilmRoll01Icon,
  FilterIcon,
  FingerPrintIcon,
  Fire02Icon,
  FireExtinguisherIcon,
  FireIcon,
  Flag01Icon,
  FlashIcon,
  FlaskConicalIcon,
  FloppyDiskIcon,
  FlowConnectionIcon,
  Folder01Icon,
  Folder02Icon,
  FolderAddIcon,
  FolderMinusIcon,
  FolderOpenIcon,
  FoldersIcon,
  FootprintsIcon,
  FullScreenIcon,
  FunctionIcon,
  GavelIcon,
  GitBranchIcon,
  GitMergeIcon,
  Globe02Icon,
  GlobeIcon,
  GraduationCapIcon,
  Grid2X2Icon,
  Grid3X3Icon,
  HammerIcon,
  HandIcon,
  HandPointingRight01Icon,
  HardDriveIcon,
  HardHatIcon,
  HashtagIcon,
  Heading02Icon,
  Heading03Icon,
  HeadingIcon,
  HeadphonesIcon,
  HeadsetIcon,
  HelpCircleIcon,
  HierarchyIcon,
  GiftIcon as HiGiftIcon,
  HistoryIcon,
  Home01Icon,
  HourglassIcon,
  House01Icon,
  IdCardLanyardIcon,
  Idea01Icon,
  IdentityCardIcon,
  Image01Icon,
  Image02Icon,
  InboxIcon,
  InformationCircleIcon,
  InternetAntenna01Icon,
  JusticeScale01Icon,
  KanbanIcon,
  Key01Icon,
  KeyboardIcon,
  LampIcon,
  LaughingIcon,
  Layers01Icon,
  Layers02Icon,
  LayoutGridIcon,
  LayoutTwoColumnIcon,
  LayoutTwoRowIcon,
  LeftToRightListBulletIcon,
  LeftToRightListDashIcon,
  LeftToRightListNumberIcon,
  Link01Icon,
  Link02Icon,
  LinkSquare02Icon,
  ListPlusIcon,
  Loading01Icon,
  Loading02Icon,
  Loading03Icon,
  Location01Icon,
  Location04Icon,
  LockIcon,
  LockKeyIcon,
  Login01Icon,
  Logout01Icon,
  MagicWand01Icon,
  Mail01Icon,
  Mail02Icon,
  MailOpen01Icon,
  MapPinIcon,
  MapsIcon,
  Menu01Icon,
  Message01Icon,
  Message02Icon,
  MessageBlockedIcon,
  Mic01Icon,
  Mic02Icon,
  MicOff01Icon,
  MinusSignCircleIcon,
  MinusSignIcon,
  Moon02Icon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
  MusicNote01Icon,
  MusicNote02Icon,
  Navigation01Icon,
  NeutralIcon,
  NewsIcon,
  NextIcon,
  NotebookIcon,
  NoteEditIcon,
  NoteIcon,
  Notification01Icon,
  PackageIcon,
  PaintBoardIcon,
  PaintBrush01Icon,
  PanelLeftIcon,
  PartyIcon,
  PauseCircleIcon,
  PauseIcon,
  PencilEdit01Icon,
  PencilEdit02Icon,
  PencilIcon,
  PieChart01Icon,
  PieChartIcon,
  PinIcon,
  PinOffIcon,
  PlayCircleIcon,
  PlayIcon,
  Plug01Icon,
  PlugSocketIcon,
  PreviousIcon,
  QrCodeIcon,
  Queue01Icon,
  QuoteUpIcon,
  ReceiptTextIcon,
  RecordIcon,
  Redo02Icon,
  RefreshIcon,
  RepeatIcon,
  RestaurantIcon,
  RotateClockwiseIcon,
  RotateLeft01Icon,
  Route01Icon,
  RulerIcon,
  SadIcon,
  Search01Icon,
  SecurityCheckIcon,
  SecurityWarningIcon,
  Sent02Icon,
  SentIcon,
  ServerStack01Icon,
  Settings01Icon,
  Settings02Icon,
  Share01Icon,
  Share02Icon,
  Shield01Icon,
  ShoppingCart01Icon,
  ShowerHeadIcon,
  SignatureIcon,
  SlackIcon,
  SlidersHorizontalIcon,
  SlidersVerticalIcon,
  SmartPhone01Icon,
  SmileIcon,
  SnowIcon,
  Sofa01Icon,
  SortByDown01Icon,
  SortByUp01Icon,
  SourceCodeIcon,
  SourceCodeSquareIcon,
  SparklesIcon,
  SplitIcon,
  SquareArrowUpDownIcon,
  SquareIcon,
  SquareLock01Icon,
  SquareUnlock01Icon,
  Stairs01Icon,
  StampIcon,
  StarHalfIcon,
  StarIcon,
  StopCircleIcon,
  StopIcon,
  StopWatchIcon,
  Sun01Icon,
  TableIcon,
  Tag01Icon,
  Tap01Icon,
  Target01Icon,
  TaskDaily01Icon,
  TelegramIcon,
  TerminalIcon,
  TextAlignLeftIcon,
  TextBoldIcon,
  TextFontIcon,
  TextIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  TextUnderlineIcon,
  ThermometerIcon,
  ThirdBracketIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Tick01Icon,
  TickDouble01Icon,
  Ticket01Icon,
  Timer01Icon,
  ToggleOffIcon,
  ToggleOnIcon,
  ToolboxIcon,
  TradeDownIcon,
  TradeUpIcon,
  TranslateIcon,
  Tree01Icon,
  TreePalmIcon,
  TruckIcon,
  Tv01Icon,
  UmbrellaIcon,
  UnavailableIcon,
  Undo02Icon,
  UnfoldMoreIcon,
  Unlink01Icon,
  Upload01Icon,
  UserAdd01Icon,
  UserCheck01Icon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  UserMinus01Icon,
  UserMultipleIcon,
  UserQuestion01Icon,
  UserSettings01Icon,
  Video01Icon,
  Video02Icon,
  ViewIcon,
  ViewOffIcon,
  VoiceIcon,
  VolumeHighIcon,
  VolumeOffIcon,
  Wallet01Icon,
  WarehouseIcon,
  WashingMachineIcon,
  WaveIcon,
  WavingHand01Icon,
  WebhookIcon,
  WhatsappIcon,
  Wifi01Icon,
  WifiDisconnected01Icon,
  Wrench01Icon,
  Xls01Icon,
} from '@hugeicons/core-free-icons'
import type { IconSvgElement } from '@hugeicons/react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  type ComponentPropsWithoutRef,
  createContext,
  type FunctionComponent,
  type RefAttributes,
  use,
} from 'react'

export type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'

export interface IconProps extends ComponentPropsWithoutRef<'svg'> {
  /** Accessible label; sets role="img" + aria-label. */
  alt?: string
  /** Stroke color. Defaults to currentColor. */
  color?: string
  /** Flips the icon horizontally (RTL affordances). */
  mirrored?: boolean
  /** Rendered width/height in px (viewBox stays 24). Defaults to 16. */
  size?: number | string
  /** Phosphor weight. Free HugeIcons are stroke-only: 'bold' -> 1.8, all others -> 1.2. */
  weight?: IconWeight
}

export type Icon = FunctionComponent<IconProps & RefAttributes<SVGSVGElement>>

/** Drop-in for Phosphor's IconContext: provides default icon props to a subtree. */
export const IconContext = createContext<IconProps>({})

const STROKE_WIDTH_BY_WEIGHT: Record<IconWeight, number> = {
  thin: 1.2,
  light: 1.2,
  regular: 1.2,
  bold: 1.8,
  fill: 1.2,
  duotone: 1.2,
}

/** Pin the painted stroke to screen px so CSS-resized icons keep their weight. */
function pinStroke(icon: IconSvgElement | undefined): IconSvgElement {
  if (!Array.isArray(icon)) {
    return []
  }
  return icon.map(
    ([tag, attrs]) => [tag, { ...attrs, vectorEffect: 'non-scaling-stroke' }] as const
  )
}

function makeIcon(icon: IconSvgElement | undefined, displayName: string): Icon {
  const pinned = pinStroke(icon)
  const IconComponent: Icon = function PhosphorCompatIcon({
    size,
    color,
    weight,
    mirrored,
    alt,
    style,
    strokeWidth,
    ref,
    ...rest
  }) {
    const ctx = use(IconContext)
    const resolvedWeight = weight ?? ctx.weight ?? 'regular'
    const resolvedMirrored = mirrored ?? ctx.mirrored ?? false
    return (
      <HugeiconsIcon
        aria-label={alt}
        color={color ?? ctx.color ?? 'currentColor'}
        icon={pinned}
        ref={ref}
        role={alt ? 'img' : undefined}
        size={size ?? ctx.size ?? 16}
        strokeWidth={
          typeof strokeWidth === 'number' ? strokeWidth : STROKE_WIDTH_BY_WEIGHT[resolvedWeight]
        }
        style={resolvedMirrored ? { transform: 'scaleX(-1)', ...style } : style}
        {...rest}
      />
    )
  }
  IconComponent.displayName = displayName
  return IconComponent
}

export const AddressBook = makeIcon(AddressBookIcon, 'AddressBook')
export const Airplane = makeIcon(Airplane01Icon, 'Airplane')
export const AppWindow = makeIcon(AppWindowIcon, 'AppWindow')
export const Archive = makeIcon(ArchiveIcon, 'Archive')
export const Armchair = makeIcon(ArmchairIcon, 'Armchair')
export const ArrowBendDoubleUpLeft = makeIcon(ArrowTurnBackwardIcon, 'ArrowBendDoubleUpLeft') // icon-review: resolved — no double-bend arrow in free set (MailReplyAll0X carries an envelope); use sites pair it with a "Reply all" label
export const ArrowBendUpLeft = makeIcon(ArrowTurnBackwardIcon, 'ArrowBendUpLeft')
export const ArrowBendUpRight = makeIcon(ArrowTurnForwardIcon, 'ArrowBendUpRight')
export const ArrowCircleUp = makeIcon(CircleArrowUp01Icon, 'ArrowCircleUp')
export const ArrowClockwise = makeIcon(RotateClockwiseIcon, 'ArrowClockwise')
export const ArrowCounterClockwise = makeIcon(RotateLeft01Icon, 'ArrowCounterClockwise')
export const ArrowDown = makeIcon(ArrowDown02Icon, 'ArrowDown')
export const ArrowElbowDownRight = makeIcon(CornerDownRightIcon, 'ArrowElbowDownRight')
export const ArrowLeft = makeIcon(ArrowLeft02Icon, 'ArrowLeft')
export const ArrowLineDown = makeIcon(ArrowDownToLineIcon, 'ArrowLineDown')
export const ArrowLineRight = makeIcon(ArrowRightToLineIcon, 'ArrowLineRight')
export const ArrowLineUp = makeIcon(ArrowUpToLineIcon, 'ArrowLineUp')
export const ArrowRight = makeIcon(ArrowRight02Icon, 'ArrowRight')
export const ArrowSquareOut = makeIcon(LinkSquare02Icon, 'ArrowSquareOut')
export const ArrowUUpLeft = makeIcon(Undo02Icon, 'ArrowUUpLeft')
export const ArrowUUpRight = makeIcon(Redo02Icon, 'ArrowUUpRight')
export const ArrowUp = makeIcon(ArrowUp02Icon, 'ArrowUp')
export const ArrowUpRight = makeIcon(ArrowUpRight01Icon, 'ArrowUpRight')
export const ArrowsClockwise = makeIcon(RefreshIcon, 'ArrowsClockwise')
export const ArrowsDownUp = makeIcon(ArrowUpDownIcon, 'ArrowsDownUp')
export const ArrowsIn = makeIcon(ArrowShrinkIcon, 'ArrowsIn')
export const ArrowsInSimple = makeIcon(ArrowShrink01Icon, 'ArrowsInSimple')
export const ArrowsLeftRight = makeIcon(ArrowLeftRightIcon, 'ArrowsLeftRight')
export const ArrowsOut = makeIcon(ArrowExpandIcon, 'ArrowsOut')
export const ArrowsOutLineHorizontal = makeIcon(ArrowHorizontalIcon, 'ArrowsOutLineHorizontal')
export const ArrowsOutSimple = makeIcon(ArrowExpand01Icon, 'ArrowsOutSimple')
export const ArrowsSplit = makeIcon(SplitIcon, 'ArrowsSplit')
export const At = makeIcon(AtIcon, 'At')
export const Baby = makeIcon(Baby01Icon, 'Baby')
export const Bank = makeIcon(BankIcon, 'Bank')
export const Barbell = makeIcon(Dumbbell01Icon, 'Barbell')
export const Bathtub = makeIcon(Bathtub01Icon, 'Bathtub')
export const BatteryHigh = makeIcon(BatteryFullIcon, 'BatteryHigh')
export const BatteryLow = makeIcon(BatteryLowIcon, 'BatteryLow')
export const Bed = makeIcon(BedIcon, 'Bed')
export const Bell = makeIcon(Notification01Icon, 'Bell')
export const BellRinging = makeIcon(BellRingIcon, 'BellRinging')
export const BookOpen = makeIcon(BookOpen01Icon, 'BookOpen')
export const BookOpenText = makeIcon(BookOpenTextIcon, 'BookOpenText')
export const BookmarkSimple = makeIcon(Bookmark01Icon, 'BookmarkSimple')
export const BracketsCurly = makeIcon(ThirdBracketIcon, 'BracketsCurly') // icon-review: resolved — ThirdBracket path has the mid-height curly pinch (SecondBracket is the square pair)
export const Brain = makeIcon(BrainIcon, 'Brain')
export const Briefcase = makeIcon(Briefcase01Icon, 'Briefcase')
export const Broadcast = makeIcon(InternetAntenna01Icon, 'Broadcast') // icon-review: resolved — antenna mast with radiating arcs matches phosphor Broadcast; Wireless renders a router box
export const Broom = makeIcon(CleanIcon, 'Broom')
export const Browsers = makeIcon(BrowserIcon, 'Browsers')
export const Bug = makeIcon(Bug01Icon, 'Bug')
export const Building = makeIcon(Building01Icon, 'Building')
export const Buildings = makeIcon(Building06Icon, 'Buildings') // icon-review: resolved — Building06 is two adjacent buildings with windows + door, matching phosphor Buildings; City01 reads slant-roof "city block"
export const Calendar = makeIcon(Calendar03Icon, 'Calendar')
export const CalendarBlank = makeIcon(Calendar01Icon, 'CalendarBlank')
export const CalendarCheck = makeIcon(CalendarCheckIcon, 'CalendarCheck')
export const CalendarDots = makeIcon(Calendar02Icon, 'CalendarDots')
export const CalendarPlus = makeIcon(CalendarAdd01Icon, 'CalendarPlus')
export const CalendarX = makeIcon(CalendarRemove01Icon, 'CalendarX')
export const Camera = makeIcon(Camera01Icon, 'Camera')
export const Car = makeIcon(Car01Icon, 'Car')
export const CaretDoubleDown = makeIcon(ArrowDownDoubleIcon, 'CaretDoubleDown')
export const CaretDoubleLeft = makeIcon(ArrowLeftDoubleIcon, 'CaretDoubleLeft')
export const CaretDoubleRight = makeIcon(ArrowRightDoubleIcon, 'CaretDoubleRight')
export const CaretDown = makeIcon(ArrowDown01Icon, 'CaretDown')
export const CaretLeft = makeIcon(ArrowLeft01Icon, 'CaretLeft')
export const CaretRight = makeIcon(ArrowRight01Icon, 'CaretRight')
export const CaretUp = makeIcon(ArrowUp01Icon, 'CaretUp')
export const CaretUpDown = makeIcon(UnfoldMoreIcon, 'CaretUpDown')
export const ChartBar = makeIcon(ChartColumnIcon, 'ChartBar')
export const ChartLine = makeIcon(ChartLineIcon, 'ChartLine')
export const ChartLineUp = makeIcon(ChartUpIcon, 'ChartLineUp')
export const ChartPie = makeIcon(PieChartIcon, 'ChartPie')
export const ChartPieSlice = makeIcon(PieChart01Icon, 'ChartPieSlice')
export const Chat = makeIcon(Message01Icon, 'Chat')
export const ChatCenteredText = makeIcon(Message02Icon, 'ChatCenteredText')
export const ChatCircle = makeIcon(BubbleChatIcon, 'ChatCircle')
export const ChatCircleDots = makeIcon(Chatting01Icon, 'ChatCircleDots')
export const ChatCircleText = makeIcon(BubbleChatIcon, 'ChatCircleText')
export const ChatDots = makeIcon(Chatting01Icon, 'ChatDots')
export const ChatSlash = makeIcon(MessageBlockedIcon, 'ChatSlash')
export const ChatTeardrop = makeIcon(Comment02Icon, 'ChatTeardrop') // icon-review: resolved — no teardrop bubble in free set; Comment02 is the PLAIN bubble (mapping was inverted with ChatTeardropText)
export const ChatTeardropDots = makeIcon(Chatting01Icon, 'ChatTeardropDots') // icon-review: resolved — best free-set match; bubble-with-dots, teardrop shape unavailable
export const ChatTeardropText = makeIcon(Comment01Icon, 'ChatTeardropText') // icon-review: resolved — Comment01 carries the text lines (mapping was inverted with ChatTeardrop)
export const ChatText = makeIcon(Message02Icon, 'ChatText')
export const ChatsCircle = makeIcon(ConversationIcon, 'ChatsCircle')
export const Check = makeIcon(Tick01Icon, 'Check')
export const CheckCircle = makeIcon(CheckmarkCircle01Icon, 'CheckCircle')
export const CheckSquare = makeIcon(CheckmarkSquare01Icon, 'CheckSquare')
export const Checks = makeIcon(TickDouble01Icon, 'Checks')
export const Circle = makeIcon(CircleIcon, 'Circle')
export const CircleDashed = makeIcon(CircleDashedIcon, 'CircleDashed')
export const CircleNotch = makeIcon(Loading02Icon, 'CircleNotch')
export const ClipboardText = makeIcon(TaskDaily01Icon, 'ClipboardText')
export const Clock = makeIcon(Clock01Icon, 'Clock')
export const ClockCountdown = makeIcon(StopWatchIcon, 'ClockCountdown') // icon-review: resolved — best free-set match; no dashed-segment countdown clock (Timer01 is a play-timer, Timer02 a near-identical stopwatch)
export const ClockCounterClockwise = makeIcon(HistoryIcon, 'ClockCounterClockwise')
export const CloudArrowUp = makeIcon(CloudUploadIcon, 'CloudArrowUp')
export const Code = makeIcon(CodeIcon, 'Code')
export const CodeBlock = makeIcon(SourceCodeSquareIcon, 'CodeBlock')
export const CodeSimple = makeIcon(SourceCodeIcon, 'CodeSimple')
export const Coins = makeIcon(Coins01Icon, 'Coins')
export const Coffee = makeIcon(Coffee01Icon, 'Coffee')
export const Columns = makeIcon(LayoutTwoColumnIcon, 'Columns')
export const Compass = makeIcon(CompassIcon, 'Compass')
export const CompassTool = makeIcon(DraftingCompassIcon, 'CompassTool')
export const ConfettiIcon = makeIcon(PartyIcon, 'ConfettiIcon')
export const CookingPot = makeIcon(CookingPotIcon, 'CookingPot')
export const Copy = makeIcon(Copy01Icon, 'Copy')
export const CopySimple = makeIcon(Copy02Icon, 'CopySimple')
export const CornersOut = makeIcon(FullScreenIcon, 'CornersOut')
export const Couch = makeIcon(Sofa01Icon, 'Couch')
export const Cpu = makeIcon(CpuIcon, 'Cpu')
export const CreditCard = makeIcon(CreditCardIcon, 'CreditCard')
export const Crown = makeIcon(CrownIcon, 'Crown')
export const Cube = makeIcon(CubeIcon, 'Cube')
export const CurrencyCircleDollar = makeIcon(DollarCircleIcon, 'CurrencyCircleDollar')
export const CurrencyDollar = makeIcon(Dollar01Icon, 'CurrencyDollar')
export const CurrencyEur = makeIcon(EuroIcon, 'CurrencyEur')
export const CursorClick = makeIcon(Tap01Icon, 'CursorClick')
export const CursorRectangleSelection = makeIcon(
  CursorRectangleSelection01Icon,
  'CursorRectangleSelection'
)
export const Database = makeIcon(DatabaseIcon, 'Database')
export const Desktop = makeIcon(ComputerIcon, 'Desktop')
export const DeviceMobile = makeIcon(SmartPhone01Icon, 'DeviceMobile')
export const Door = makeIcon(Door01Icon, 'Door')
export const DotsSix = makeIcon(DragDropHorizontalIcon, 'DotsSix')
export const DotsSixVertical = makeIcon(DragDropVerticalIcon, 'DotsSixVertical')
export const DotsThree = makeIcon(MoreHorizontalIcon, 'DotsThree')
export const DotsThreeOutline = makeIcon(MoreHorizontalIcon, 'DotsThreeOutline') // icon-review: resolved — stroke-only set cannot express the filled/outline dot distinction; same glyph as DotsThree is correct
export const DotsThreeVertical = makeIcon(MoreVerticalIcon, 'DotsThreeVertical')
export const DownloadSimple = makeIcon(Download01Icon, 'DownloadSimple')
export const Drop = makeIcon(DropletIcon, 'Drop')
export const Elevator = makeIcon(SquareArrowUpDownIcon, 'Elevator') // icon-review: resolved — best free-set match; no elevator/lift glyph (only Forklift/LiftTruck); box with up/down arrows is the standard metaphor
export const Envelope = makeIcon(Mail01Icon, 'Envelope')
export const EnvelopeOpen = makeIcon(MailOpen01Icon, 'EnvelopeOpen')
export const EnvelopeSimple = makeIcon(Mail02Icon, 'EnvelopeSimple')
export const Export = makeIcon(FileExportIcon, 'Export')
export const Eye = makeIcon(ViewIcon, 'Eye')
export const EyeSlash = makeIcon(ViewOffIcon, 'EyeSlash')
export const Fan = makeIcon(Fan01Icon, 'Fan')
export const File = makeIcon(File01Icon, 'File')
export const FileArrowUp = makeIcon(FileUploadIcon, 'FileArrowUp')
export const FileCode = makeIcon(FileCodeIcon, 'FileCode')
export const FileCsv = makeIcon(Csv01Icon, 'FileCsv')
export const FileImage = makeIcon(File02Icon, 'FileImage')
export const FilePlus = makeIcon(FileAddIcon, 'FilePlus')
export const FileText = makeIcon(File02Icon, 'FileText')
export const FileXls = makeIcon(Xls01Icon, 'FileXls')
export const FilmStrip = makeIcon(FilmRoll01Icon, 'FilmStrip')
export const Fingerprint = makeIcon(FingerPrintIcon, 'Fingerprint')
export const Fire = makeIcon(FireIcon, 'Fire')
export const FireExtinguisher = makeIcon(FireExtinguisherIcon, 'FireExtinguisher')
export const Flag = makeIcon(Flag01Icon, 'Flag')
export const Flame = makeIcon(Fire02Icon, 'Flame')
export const Flask = makeIcon(FlaskConicalIcon, 'Flask')
export const FloppyDisk = makeIcon(FloppyDiskIcon, 'FloppyDisk')
export const FlowArrow = makeIcon(FlowConnectionIcon, 'FlowArrow')
export const Folder = makeIcon(Folder02Icon, 'Folder')
export const FolderMinus = makeIcon(FolderMinusIcon, 'FolderMinus')
export const FolderOpen = makeIcon(FolderOpenIcon, 'FolderOpen')
export const FolderPlus = makeIcon(FolderAddIcon, 'FolderPlus')
export const FolderSimple = makeIcon(Folder01Icon, 'FolderSimple')
export const Folders = makeIcon(FoldersIcon, 'Folders')
export const Footprints = makeIcon(FootprintsIcon, 'Footprints')
export const ForkKnife = makeIcon(RestaurantIcon, 'ForkKnife')
export const Function = makeIcon(FunctionIcon, 'Function')
export const FunnelSimple = makeIcon(FilterIcon, 'FunnelSimple')
export const Gauge = makeIcon(DashboardSpeed01Icon, 'Gauge')
export const Gavel = makeIcon(GavelIcon, 'Gavel')
export const Gear = makeIcon(Settings01Icon, 'Gear')
export const GearSix = makeIcon(Settings02Icon, 'GearSix')
export const GiftIcon = makeIcon(HiGiftIcon, 'GiftIcon')
export const GitBranch = makeIcon(GitBranchIcon, 'GitBranch')
export const GitMerge = makeIcon(GitMergeIcon, 'GitMerge')
export const Globe = makeIcon(GlobeIcon, 'Globe')
export const GlobeHemisphereWest = makeIcon(Globe02Icon, 'GlobeHemisphereWest')
export const GraduationCap = makeIcon(GraduationCapIcon, 'GraduationCap')
export const GridFour = makeIcon(Grid2X2Icon, 'GridFour')
export const GridNine = makeIcon(Grid3X3Icon, 'GridNine')
export const Hammer = makeIcon(HammerIcon, 'Hammer')
export const Hand = makeIcon(HandIcon, 'Hand')
export const HandPalm = makeIcon(HandIcon, 'HandPalm')
export const HandPointing = makeIcon(HandPointingRight01Icon, 'HandPointing') // icon-review: resolved — free set only has directional pointing hands (no up-pointing plain hand); Tap0X variants would duplicate CursorClick
export const HandWaving = makeIcon(WavingHand01Icon, 'HandWaving')
export const Handshake = makeIcon(Agreement01Icon, 'Handshake')
export const HardDrive = makeIcon(HardDriveIcon, 'HardDrive')
export const HardDrives = makeIcon(ServerStack01Icon, 'HardDrives')
export const HardHat = makeIcon(HardHatIcon, 'HardHat')
export const Hash = makeIcon(HashtagIcon, 'Hash')
export const Headphones = makeIcon(HeadphonesIcon, 'Headphones')
export const Headset = makeIcon(HeadsetIcon, 'Headset')
export const Heart = makeIcon(FavouriteIcon, 'Heart')
export const Heartbeat = makeIcon(Cardiogram01Icon, 'Heartbeat')
export const Hourglass = makeIcon(HourglassIcon, 'Hourglass')
export const House = makeIcon(Home01Icon, 'House')
export const HouseLine = makeIcon(House01Icon, 'HouseLine')
export const IdentificationBadge = makeIcon(IdCardLanyardIcon, 'IdentificationBadge')
export const IdentificationCard = makeIcon(IdentityCardIcon, 'IdentificationCard')
export const Image = makeIcon(Image01Icon, 'Image')
export const ImageIcon = makeIcon(Image01Icon, 'ImageIcon')
export const ImageSquare = makeIcon(Image02Icon, 'ImageSquare')
export const ImagesSquare = makeIcon(Album01Icon, 'ImagesSquare') // icon-review: resolved — Album01 renders two overlapping framed photos, the faithful stacked-images glyph
export const Info = makeIcon(InformationCircleIcon, 'Info')
export const Kanban = makeIcon(KanbanIcon, 'Kanban')
export const Key = makeIcon(Key01Icon, 'Key')
export const Keyboard = makeIcon(KeyboardIcon, 'Keyboard')
export const Lamp = makeIcon(LampIcon, 'Lamp')
export const Lightbulb = makeIcon(BulbIcon, 'Lightbulb')
export const LightbulbFilament = makeIcon(Idea01Icon, 'LightbulbFilament')
export const Lightning = makeIcon(FlashIcon, 'Lightning')
export const Link = makeIcon(Link02Icon, 'Link')
export const LinkBreak = makeIcon(Unlink01Icon, 'LinkBreak')
export const LinkSimple = makeIcon(Link01Icon, 'LinkSimple')
export const List = makeIcon(Menu01Icon, 'List')
export const ListBullets = makeIcon(LeftToRightListBulletIcon, 'ListBullets')
export const ListChecks = makeIcon(CheckListIcon, 'ListChecks')
export const ListDashes = makeIcon(LeftToRightListDashIcon, 'ListDashes')
export const ListNumbers = makeIcon(LeftToRightListNumberIcon, 'ListNumbers')
export const ListPlus = makeIcon(ListPlusIcon, 'ListPlus')
export const Lock = makeIcon(LockIcon, 'Lock')
export const LockKey = makeIcon(LockKeyIcon, 'LockKey')
export const LockOpen = makeIcon(SquareUnlock01Icon, 'LockOpen')
export const LockSimple = makeIcon(SquareLock01Icon, 'LockSimple')
export const MagicWand = makeIcon(MagicWand01Icon, 'MagicWand')
export const MagnifyingGlass = makeIcon(Search01Icon, 'MagnifyingGlass')
export const MapPin = makeIcon(Location01Icon, 'MapPin')
export const MapPinLine = makeIcon(Location04Icon, 'MapPinLine')
export const MapPinSimple = makeIcon(MapPinIcon, 'MapPinSimple')
export const MapTrifold = makeIcon(MapsIcon, 'MapTrifold')
export const MarkdownLogo = makeIcon(DocumentCodeIcon, 'MarkdownLogo') // icon-review: resolved — best free-set match; no markdown M-down logo; document-with-code reads "markup file" at the attachment use site
export const Microphone = makeIcon(Mic01Icon, 'Microphone')
export const MicrophoneSlash = makeIcon(MicOff01Icon, 'MicrophoneSlash')
export const MicrophoneStage = makeIcon(Mic02Icon, 'MicrophoneStage') // icon-review: resolved — best free-set match; no handheld/stage mic glyph, studio mic is the closest speaker metaphor
export const Minus = makeIcon(MinusSignIcon, 'Minus')
export const MinusCircle = makeIcon(MinusSignCircleIcon, 'MinusCircle')
export const Monitor = makeIcon(ComputerIcon, 'Monitor')
export const Moon = makeIcon(Moon02Icon, 'Moon')
export const MusicNote = makeIcon(MusicNote01Icon, 'MusicNote')
export const MusicNoteSimple = makeIcon(MusicNote02Icon, 'MusicNoteSimple')
export const NavigationArrow = makeIcon(Navigation01Icon, 'NavigationArrow')
export const Newspaper = makeIcon(NewsIcon, 'Newspaper')
export const Note = makeIcon(NoteIcon, 'Note')
export const NotePencil = makeIcon(NoteEditIcon, 'NotePencil')
export const Notebook = makeIcon(NotebookIcon, 'Notebook')
export const Package = makeIcon(PackageIcon, 'Package')
export const PaintBrush = makeIcon(PaintBrush01Icon, 'PaintBrush')
export const Palette = makeIcon(PaintBoardIcon, 'Palette')
export const PaperPlaneRight = makeIcon(Sent02Icon, 'PaperPlaneRight')
export const PaperPlaneTilt = makeIcon(SentIcon, 'PaperPlaneTilt')
export const Paperclip = makeIcon(AttachmentIcon, 'Paperclip')
export const Path = makeIcon(Route01Icon, 'Path')
export const Pause = makeIcon(PauseIcon, 'Pause')
export const PauseCircle = makeIcon(PauseCircleIcon, 'PauseCircle')
export const Pencil = makeIcon(PencilIcon, 'Pencil')
export const PencilSimple = makeIcon(PencilEdit02Icon, 'PencilSimple')
export const PencilSimpleLine = makeIcon(PencilEdit01Icon, 'PencilSimpleLine')
export const Phone = makeIcon(CallIcon, 'Phone')
export const PhoneCall = makeIcon(CallRinging01Icon, 'PhoneCall')
export const PhoneIncoming = makeIcon(CallIncoming01Icon, 'PhoneIncoming')
export const PhoneOutgoing = makeIcon(CallOutgoing01Icon, 'PhoneOutgoing')
export const PhoneSlash = makeIcon(CallDisabledIcon, 'PhoneSlash')
export const PhoneTransfer = makeIcon(CallInternalIcon, 'PhoneTransfer') // icon-review: resolved — CallInternal renders a handset with double-headed transfer arrows, the closest call-transfer glyph
export const PhoneX = makeIcon(CallBlockedIcon, 'PhoneX')
export const Play = makeIcon(PlayIcon, 'Play')
export const PlayCircle = makeIcon(PlayCircleIcon, 'PlayCircle')
export const Plug = makeIcon(Plug01Icon, 'Plug')
export const Plugs = makeIcon(PlugSocketIcon, 'Plugs')
export const PlugsConnected = makeIcon(ElectricPlugsIcon, 'PlugsConnected')
export const Plus = makeIcon(Add01Icon, 'Plus')
export const Prohibit = makeIcon(UnavailableIcon, 'Prohibit')
export const ProhibitInset = makeIcon(UnavailableIcon, 'ProhibitInset')
export const Pulse = makeIcon(Activity01Icon, 'Pulse')
export const PushPin = makeIcon(PinIcon, 'PushPin')
export const PushPinSlash = makeIcon(PinOffIcon, 'PushPinSlash')
export const QrCode = makeIcon(QrCodeIcon, 'QrCode')
export const Question = makeIcon(HelpCircleIcon, 'Question')
export const Queue = makeIcon(Queue01Icon, 'Queue')
export const Quotes = makeIcon(QuoteUpIcon, 'Quotes')
export const Receipt = makeIcon(ReceiptTextIcon, 'Receipt')
export const Record = makeIcon(RecordIcon, 'Record')
export const Repeat = makeIcon(RepeatIcon, 'Repeat')
export const Robot = makeIcon(BotIcon, 'Robot')
export const Rows = makeIcon(LayoutTwoRowIcon, 'Rows')
export const Ruler = makeIcon(RulerIcon, 'Ruler')
export const Scales = makeIcon(JusticeScale01Icon, 'Scales')
export const SealCheck = makeIcon(CheckmarkBadge01Icon, 'SealCheck')
export const Share = makeIcon(Share01Icon, 'Share')
export const ShareNetwork = makeIcon(Share02Icon, 'ShareNetwork')
export const Shield = makeIcon(Shield01Icon, 'Shield')
export const ShieldCheck = makeIcon(SecurityCheckIcon, 'ShieldCheck')
export const ShieldWarning = makeIcon(SecurityWarningIcon, 'ShieldWarning')
export const ShoppingCart = makeIcon(ShoppingCart01Icon, 'ShoppingCart')
export const Shower = makeIcon(ShowerHeadIcon, 'Shower')
export const SidebarSimple = makeIcon(PanelLeftIcon, 'SidebarSimple')
export const SignIn = makeIcon(Login01Icon, 'SignIn')
export const SignOut = makeIcon(Logout01Icon, 'SignOut')
export const Signature = makeIcon(SignatureIcon, 'Signature')
export const SkipBack = makeIcon(PreviousIcon, 'SkipBack')
export const SkipForward = makeIcon(NextIcon, 'SkipForward')
export const SlackLogo = makeIcon(SlackIcon, 'SlackLogo')
export const Sliders = makeIcon(SlidersVerticalIcon, 'Sliders')
export const SlidersHorizontal = makeIcon(SlidersHorizontalIcon, 'SlidersHorizontal')
export const Smiley = makeIcon(SmileIcon, 'Smiley')
export const SmileyAngry = makeIcon(AngryIcon, 'SmileyAngry')
export const SmileyNeutral = makeIcon(NeutralIcon, 'SmileyNeutral')
export const SmileySad = makeIcon(SadIcon, 'SmileySad')
export const SmileyVeryHappy = makeIcon(LaughingIcon, 'SmileyVeryHappy')
export const Snowflake = makeIcon(SnowIcon, 'Snowflake')
export const SortAscending = makeIcon(SortByUp01Icon, 'SortAscending')
export const SortDescending = makeIcon(SortByDown01Icon, 'SortDescending')
export const Sparkle = makeIcon(SparklesIcon, 'Sparkle')
export const SpeakerHigh = makeIcon(VolumeHighIcon, 'SpeakerHigh')
export const SpeakerSlash = makeIcon(VolumeOffIcon, 'SpeakerSlash')
export const Spinner = makeIcon(Loading01Icon, 'Spinner')
export const SpinnerGap = makeIcon(Loading03Icon, 'SpinnerGap')
export const Square = makeIcon(SquareIcon, 'Square')
export const SquaresFour = makeIcon(LayoutGridIcon, 'SquaresFour')
export const Stack = makeIcon(Layers01Icon, 'Stack')
export const StackSimple = makeIcon(Layers02Icon, 'StackSimple')
export const Stairs = makeIcon(Stairs01Icon, 'Stairs')
export const Stamp = makeIcon(StampIcon, 'Stamp')
export const Star = makeIcon(StarIcon, 'Star')
export const StarHalf = makeIcon(StarHalfIcon, 'StarHalf')
export const Stop = makeIcon(StopIcon, 'Stop')
export const StopCircle = makeIcon(StopCircleIcon, 'StopCircle')
export const Sun = makeIcon(Sun01Icon, 'Sun')
export const Table = makeIcon(TableIcon, 'Table')
export const Tag = makeIcon(Tag01Icon, 'Tag')
export const Target = makeIcon(Target01Icon, 'Target')
export const TelegramLogo = makeIcon(TelegramIcon, 'TelegramLogo')
export const Television = makeIcon(Tv01Icon, 'Television')
export const Terminal = makeIcon(TerminalIcon, 'Terminal')
export const TextAa = makeIcon(TextFontIcon, 'TextAa')
export const TextAlignLeft = makeIcon(TextAlignLeftIcon, 'TextAlignLeft')
export const TextB = makeIcon(TextBoldIcon, 'TextB')
export const TextH = makeIcon(HeadingIcon, 'TextH')
export const TextHThree = makeIcon(Heading03Icon, 'TextHThree')
export const TextHTwo = makeIcon(Heading02Icon, 'TextHTwo')
export const TextItalic = makeIcon(TextItalicIcon, 'TextItalic')
export const TextStrikethrough = makeIcon(TextStrikethroughIcon, 'TextStrikethrough')
export const TextT = makeIcon(TextIcon, 'TextT')
export const TextUnderline = makeIcon(TextUnderlineIcon, 'TextUnderline')
export const Thermometer = makeIcon(ThermometerIcon, 'Thermometer')
export const ThumbsDown = makeIcon(ThumbsDownIcon, 'ThumbsDown')
export const ThumbsUp = makeIcon(ThumbsUpIcon, 'ThumbsUp')
export const Ticket = makeIcon(Ticket01Icon, 'Ticket')
export const Timer = makeIcon(Timer01Icon, 'Timer')
export const ToggleLeft = makeIcon(ToggleOffIcon, 'ToggleLeft')
export const ToggleRight = makeIcon(ToggleOnIcon, 'ToggleRight')
export const Toolbox = makeIcon(ToolboxIcon, 'Toolbox')
export const Translate = makeIcon(TranslateIcon, 'Translate')
export const Trash = makeIcon(Delete02Icon, 'Trash')
export const Tray = makeIcon(InboxIcon, 'Tray')
export const Tree = makeIcon(Tree01Icon, 'Tree')
export const TreePalm = makeIcon(TreePalmIcon, 'TreePalm')
export const TreeStructure = makeIcon(HierarchyIcon, 'TreeStructure')
export const TrendDown = makeIcon(TradeDownIcon, 'TrendDown')
export const TrendUp = makeIcon(TradeUpIcon, 'TrendUp')
export const Trophy = makeIcon(ChampionIcon, 'Trophy')
export const Truck = makeIcon(TruckIcon, 'Truck')
export const Umbrella = makeIcon(UmbrellaIcon, 'Umbrella')
export const UploadSimple = makeIcon(Upload01Icon, 'UploadSimple')
export const User = makeIcon(UserIcon, 'User')
export const UserCheck = makeIcon(UserCheck01Icon, 'UserCheck')
export const UserCircle = makeIcon(UserCircleIcon, 'UserCircle')
export const UserCircleDashed = makeIcon(UserQuestion01Icon, 'UserCircleDashed') // icon-review: resolved — no dashed-ring user glyph; user-with-question keeps the "unassigned/unknown" semantic and stays distinct from UserCircle (they previously rendered identically in the same dropdown)
export const UserGear = makeIcon(UserSettings01Icon, 'UserGear')
export const UserMinus = makeIcon(UserMinus01Icon, 'UserMinus')
export const UserPlus = makeIcon(UserAdd01Icon, 'UserPlus')
export const Users = makeIcon(UserMultipleIcon, 'Users')
export const UsersThree = makeIcon(UserGroupIcon, 'UsersThree')
export const Video = makeIcon(Video02Icon, 'Video')
export const VideoCamera = makeIcon(Video01Icon, 'VideoCamera')
export const Voicemail = makeIcon(VoiceIcon, 'Voicemail') // icon-review: resolved — best free-set match; no reel voicemail glyph; waveform square reads "voice recording" (MailVoice0X adds an envelope, too busy at 16px)
export const Wall = makeIcon(BrickWallIcon, 'Wall')
export const Wallet = makeIcon(Wallet01Icon, 'Wallet')
export const Warehouse = makeIcon(WarehouseIcon, 'Warehouse')
export const Warning = makeIcon(Alert01Icon, 'Warning')
export const WarningCircle = makeIcon(AlertCircleIcon, 'WarningCircle')
export const WarningOctagon = makeIcon(AlertDiamondIcon, 'WarningOctagon') // icon-review: resolved — free set has only plain Octagon/OctagonX; keeping the exclamation (warning semantic) beats matching the shape
export const WashingMachine = makeIcon(WashingMachineIcon, 'WashingMachine')
export const Waveform = makeIcon(AudioWave01Icon, 'Waveform')
export const Waves = makeIcon(WaveIcon, 'Waves')
export const WebhooksLogo = makeIcon(WebhookIcon, 'WebhooksLogo')
export const WhatsappLogo = makeIcon(WhatsappIcon, 'WhatsappLogo')
export const WifiHigh = makeIcon(Wifi01Icon, 'WifiHigh')
export const WifiSlash = makeIcon(WifiDisconnected01Icon, 'WifiSlash')
export const Wind = makeIcon(FastWindIcon, 'Wind')
export const Wrench = makeIcon(Wrench01Icon, 'Wrench')
export const X = makeIcon(Cancel01Icon, 'X')
export const XCircle = makeIcon(CancelCircleIcon, 'XCircle')
